import { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { UserContext } from '../contexts/UserContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useRegisterPullToRefresh } from '../hooks/useRegisterPullToRefresh';
import { useOffersAwaitingResponse } from '../hooks/useOffersAwaitingResponse';
import { isListingInterestEnabled } from '../featureFlags';
import { isInterestFresh } from '../utils/listingInterestPolicy';
import Footer from '../components/Footer';
import { placeholderFor } from '../utils/listingPlaceholder';

const TradingHub = () => {
  const { firestoreUser } = useContext(UserContext);
  const { notifications, markMatchNotificationsAsRead } = useNotifications();
  const [currentUser, setCurrentUser] = useState(null);
  // The number on the Trading badge (navbar + bottom tab): open offers waiting
  // on this user. Shown on the Offers tile so the badge is visibly about
  // offers, not matches or shipping (owner, 2026-11-02).
  const offersAwaitingResponse = useOffersAwaitingResponse();
  const [counts, setCounts] = useState({
    newMatches: 0,
    openInterest: 0,
    sentOffers: 0,
    receivedOffers: 0,
    acceptedTrades: 0,
    completedTrades: 0,
    acceptedPurchases: 0,
    completedPurchases: 0
  });
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [matchedPedals, setMatchedPedals] = useState([]);
  // Hidden matches (both the per-match list and the per-counterparty list the
  // Matches page writes) so a hidden match does not keep showing its
  // thumbnail on the Matches tile (owner, 2026-11-01).
  const [hiddenMatchIds, setHiddenMatchIds] = useState([]);
  const [hiddenMatchUsers, setHiddenMatchUsers] = useState([]);
  const [hubRefreshNonce, setHubRefreshNonce] = useState(0);
  const interestEnabled = isListingInterestEnabled(firestoreUser);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mark match notifications as read ONLY on initial mount (when user navigates to this page)
  // Don't re-run this effect when new notifications arrive while already on this page
  useEffect(() => {
    markMatchNotificationsAsRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = only runs on mount

  // Count new match notifications
  useEffect(() => {
    if (notifications) {
      const newMatchCount = notifications.filter(n => 
        n.type === 'new_match' && !n.read
      ).length;
      setCounts(prev => ({ ...prev, newMatches: newMatchCount }));
    }
  }, [notifications]);

  // Query matches collection for BOTH sides (where I'm myPedalUser OR matchedPedalUser)
  useEffect(() => {
      if (!currentUser) return;

        const userId = currentUser.uid;
    console.log('📡 TradingHub: Query matches for user', userId);
    
    const myMatchesQuery = query(
      collection(db, 'matches'),
      where('myPedalUser', '==', userId)
    );
    const theirMatchesQuery = query(
      collection(db, 'matches'),
      where('matchedPedalUser', '==', userId)
    );

    let myMatches = [];
    let theirMatches = [];

    const updateMatches = () => {
      const allMyMatches = myMatches.map(doc => ({ id: doc.id, ...doc.data() }));
      const allTheirMatches = theirMatches.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        // Swap the pedals so the current user is always "myPedal"
        myPedal: doc.data().matchedPedal,
        matchedPedal: doc.data().myPedal
      }));

      const allMatches = [...allMyMatches, ...allTheirMatches];

      const matches = allMatches.map(matchDoc => ({
        matchId: matchDoc.id,
        counterpartyUid: matchDoc.matchedPedalUser === userId ? matchDoc.myPedalUser : matchDoc.matchedPedalUser,
        myPedal: matchDoc.myPedal,
        matchedPedal: matchDoc.matchedPedal
      }));
                    
      console.log('📥 TradingHub: Total matches:', matches.length);
      setMatchedPedals(matches);
    };

    const unsubHidden = onSnapshot(doc(db, 'users', userId), (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setHiddenMatchIds(data.hiddenMatches || []);
      setHiddenMatchUsers(data.hiddenMatchUsers || []);
    });

    const unsubMyMatches = onSnapshot(myMatchesQuery, (mySnap) => {
      myMatches = mySnap.docs;
      updateMatches();
    });

    const unsubTheirMatches = onSnapshot(theirMatchesQuery, (theirSnap) => {
      theirMatches = theirSnap.docs;
      updateMatches();
    });

    return () => {
      unsubHidden();
      unsubMyMatches();
      unsubTheirMatches();
    };
  }, [currentUser, hubRefreshNonce]);

  // Same rule as the Matches page's visibleMatches: a match is hidden by its
  // id or through its counterparty's hidden list.
  const visibleMatchedPedals = useMemo(() => matchedPedals.filter((m) => {
    if (hiddenMatchIds.includes(m.matchId)) return false;
    const entry = hiddenMatchUsers.find((h) => h.counterpartyUid === m.counterpartyUid);
    return !(entry && Array.isArray(entry.hiddenMatchIds) && entry.hiddenMatchIds.includes(m.matchId));
  }), [matchedPedals, hiddenMatchIds, hiddenMatchUsers]);

  // Listen to counts in real-time using onSnapshot
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const userId = currentUser.uid;
    console.log('🔍 TradingHub: Setting up real-time listeners for user:', userId);

    // Track counts from all listeners
    let currentCounts = {
      sentTrades: 0,
      sentPurchases: 0,
      receivedTrades: 0,
      receivedPurchases: 0,
      acceptedTradesFrom: 0,
      acceptedTradesTo: 0,
      completedTradesFrom: 0,
      completedTradesTo: 0,
      acceptedPurchasesInitiator: 0,
      acceptedPurchasesResponder: 0,
      completedPurchasesInitiator: 0,
      completedPurchasesResponder: 0,
      openInterestInbound: 0,
      openInterestReplies: 0
    };

    // Two listeners feed one number, so it's summed on its own rather than
    // inside updateCounts, which the offer listeners drive.
    const updateInterestCount = () => {
      setCounts(prev => ({
        ...prev,
        openInterest: currentCounts.openInterestInbound + currentCounts.openInterestReplies
      }));
    };

    const updateCounts = () => {
      const sentOffersCount = currentCounts.sentTrades + currentCounts.sentPurchases;
      const receivedOffersCount = currentCounts.receivedTrades + currentCounts.receivedPurchases;
      const acceptedTradesCount = currentCounts.acceptedTradesFrom + currentCounts.acceptedTradesTo;
      const completedTradesCount = currentCounts.completedTradesFrom + currentCounts.completedTradesTo;
      const acceptedPurchasesCount = currentCounts.acceptedPurchasesInitiator + currentCounts.acceptedPurchasesResponder;
      const completedPurchasesCount = currentCounts.completedPurchasesInitiator + currentCounts.completedPurchasesResponder;

      console.log('📊 TradingHub counts (DETAILED):', {
        sentTrades: currentCounts.sentTrades,
        sentPurchases: currentCounts.sentPurchases,
        sentTotal: sentOffersCount,
        receivedTrades: currentCounts.receivedTrades,
        receivedPurchases: currentCounts.receivedPurchases,
        receivedTotal: receivedOffersCount,
        acceptedTrades: acceptedTradesCount,
        completedTrades: completedTradesCount,
        acceptedPurchases: acceptedPurchasesCount,
        completedPurchases: completedPurchasesCount
      });

      setCounts(prev => ({
        ...prev,
        sentOffers: sentOffersCount,
        receivedOffers: receivedOffersCount,
        acceptedTrades: acceptedTradesCount,
        completedTrades: completedTradesCount,
        acceptedPurchases: acceptedPurchasesCount,
        completedPurchases: completedPurchasesCount
      }));
    };

    // Helper function to check if an offer is expired (older than 48 hours)
    const isOfferExpired = (createdAt) => {
      if (!createdAt) return false;
      const createdDate = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
      const expirationDate = new Date(createdDate.getTime() + (48 * 60 * 60 * 1000)); // 48 hours
      const now = new Date();
      return now > expirationDate;
    };

    // Set up all real-time listeners
    const unsubscribers = [];

    // Sent trade offers - filter out expired ones
    const sentTradesQuery = query(
      collection(db, 'tradeOffers'),
      where('fromUserId', '==', userId),
      where('status', 'in', ['pending', 'countered'])
    );
    unsubscribers.push(onSnapshot(sentTradesQuery, (snapshot) => {
      // Filter out expired offers (older than 48 hours)
      const activeOffers = snapshot.docs.filter(doc => !isOfferExpired(doc.data().createdAt));
      currentCounts.sentTrades = activeOffers.length;
      updateCounts();
    }));

    // Sent purchase offers - filter out expired ones
    const sentPurchasesQuery = query(
      collection(db, 'purchaseOffers'),
      where('initiatorId', '==', userId),
      where('status', '==', 'pending')
    );
    unsubscribers.push(onSnapshot(sentPurchasesQuery, (snapshot) => {
      // Filter out expired offers (older than 48 hours)
      const activeOffers = snapshot.docs.filter(doc => !isOfferExpired(doc.data().createdAt));
      currentCounts.sentPurchases = activeOffers.length;
      updateCounts();
    }));

    // Received trade offers - filter out expired ones
    const receivedTradesQuery = query(
      collection(db, 'tradeOffers'),
      where('toUserId', '==', userId),
      where('status', 'in', ['pending', 'countered'])
    );
    unsubscribers.push(onSnapshot(receivedTradesQuery, (snapshot) => {
      // Filter out expired offers (older than 48 hours)
      const activeOffers = snapshot.docs.filter(doc => !isOfferExpired(doc.data().createdAt));
      currentCounts.receivedTrades = activeOffers.length;
      updateCounts();
    }));

    // Received purchase offers - filter out expired ones
    const receivedPurchasesQuery = query(
      collection(db, 'purchaseOffers'),
      where('responderId', '==', userId),
      where('status', '==', 'pending')
    );
    unsubscribers.push(onSnapshot(receivedPurchasesQuery, (snapshot) => {
      // Filter out expired offers (older than 48 hours)
      const activeOffers = snapshot.docs.filter(doc => !isOfferExpired(doc.data().createdAt));
      currentCounts.receivedPurchases = activeOffers.length;
      updateCounts();
    }));

    // Accepted trades (from)
    const acceptedTradesFromQuery = query(
      collection(db, 'tradeOffers'),
      where('fromUserId', '==', userId),
      where('status', 'in', ['accepted', 'inProgress', 'payment_completed'])
    );
    unsubscribers.push(onSnapshot(acceptedTradesFromQuery, (snapshot) => {
      currentCounts.acceptedTradesFrom = snapshot.size;
      updateCounts();
    }));

    // Accepted trades (to)
    const acceptedTradesToQuery = query(
      collection(db, 'tradeOffers'),
      where('toUserId', '==', userId),
      where('status', 'in', ['accepted', 'inProgress', 'payment_completed'])
    );
    unsubscribers.push(onSnapshot(acceptedTradesToQuery, (snapshot) => {
      currentCounts.acceptedTradesTo = snapshot.size;
      updateCounts();
    }));

    // Completed trades (from)
    const completedTradesFromQuery = query(
      collection(db, 'tradeOffers'),
      where('fromUserId', '==', userId),
      where('status', 'in', ['complete', 'completed'])
    );
    unsubscribers.push(onSnapshot(completedTradesFromQuery, (snapshot) => {
      currentCounts.completedTradesFrom = snapshot.size;
      updateCounts();
    }));

    // Completed trades (to)
    const completedTradesToQuery = query(
      collection(db, 'tradeOffers'),
      where('toUserId', '==', userId),
      where('status', 'in', ['complete', 'completed'])
    );
    unsubscribers.push(onSnapshot(completedTradesToQuery, (snapshot) => {
      currentCounts.completedTradesTo = snapshot.size;
      updateCounts();
    }));

    // Accepted purchases (initiator)
    const acceptedPurchasesInitiatorQuery = query(
      collection(db, 'purchaseOffers'),
      where('initiatorId', '==', userId),
      where('status', 'in', ['accepted', 'inProgress', 'payment_completed', 'paid'])
    );
    unsubscribers.push(onSnapshot(acceptedPurchasesInitiatorQuery, (snapshot) => {
      currentCounts.acceptedPurchasesInitiator = snapshot.size;
      updateCounts();
    }));

    // Accepted purchases (responder)
    const acceptedPurchasesResponderQuery = query(
      collection(db, 'purchaseOffers'),
      where('responderId', '==', userId),
      where('status', 'in', ['accepted', 'inProgress', 'payment_completed', 'paid'])
    );
    unsubscribers.push(onSnapshot(acceptedPurchasesResponderQuery, (snapshot) => {
      currentCounts.acceptedPurchasesResponder = snapshot.size;
      updateCounts();
    }));

    // Completed purchases (initiator)
    const completedPurchasesInitiatorQuery = query(
      collection(db, 'purchaseOffers'),
      where('initiatorId', '==', userId),
      where('status', 'in', ['complete', 'completed'])
    );
    unsubscribers.push(onSnapshot(completedPurchasesInitiatorQuery, (snapshot) => {
      currentCounts.completedPurchasesInitiator = snapshot.size;
      updateCounts();
    }));

    // Completed purchases (responder)
    const completedPurchasesResponderQuery = query(
      collection(db, 'purchaseOffers'),
      where('responderId', '==', userId),
      where('status', 'in', ['complete', 'completed'])
    );
    unsubscribers.push(onSnapshot(completedPurchasesResponderQuery, (snapshot) => {
      currentCounts.completedPurchasesResponder = snapshot.size;
      updateCounts();
    }));

    // Open "I'm interested" signals on this user's listings (beta). They live on
    // the Offers page, so the card has to count them or a waiting interest looks
    // like an empty page.
    if (interestEnabled) {
      const openInterestQuery = query(
        collection(db, 'listingInterest'),
        where('listingOwnerId', '==', userId),
        where('status', '==', 'open')
      );
      unsubscribers.push(onSnapshot(openInterestQuery, (snapshot) => {
        currentCounts.openInterestInbound = snapshot.docs
          .filter((d) => isInterestFresh(d.data().createdAt)).length;
        updateInterestCount();
      }));

      // Answered interests this user sent. Also on the Offers page, and equally
      // the user's move, so they count the same as an inbound signal — otherwise
      // a reply waiting for an offer leaves the badge reading zero.
      const answeredInterestQuery = query(
        collection(db, 'listingInterest'),
        where('fromUserId', '==', userId),
        where('status', '==', 'actioned')
      );
      unsubscribers.push(onSnapshot(answeredInterestQuery, (snapshot) => {
        currentCounts.openInterestReplies = snapshot.docs.filter((d) => {
          const data = d.data();
          return Array.isArray(data.responsePedalIds) &&
            data.responsePedalIds.length > 0 &&
            isInterestFresh(data.createdAt);
        }).length;
        updateInterestCount();
      }));
    }

    setLoading(false);

    // Cleanup all listeners on unmount
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [currentUser, hubRefreshNonce, interestEnabled]);

  const refreshTradingHub = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setMatchedPedals([]);
    setHubRefreshNonce((n) => n + 1);
    markMatchNotificationsAsRead();
  }, [currentUser, markMatchNotificationsAsRead]);

  useRegisterPullToRefresh(refreshTradingHub);

  if (!authLoading && !currentUser) {
    return (
      <div style={{ position: 'fixed', top: 'var(--navbar-height, 125px)', left: 0, right: 0, bottom: 0, overflow: 'auto', backgroundColor: '#F7F3EB', paddingTop: '2rem' }}>
        <style>{`
          @keyframes modalPulse {
            0%, 100% { box-shadow: 0 8px 32px rgba(90, 158, 203, 0.3); }
            50% { box-shadow: 0 8px 48px rgba(90, 158, 203, 0.6); }
          }
          @media (max-width: 768px) {
            .preview-modal {
              max-width: 240px !important;
              padding: 1rem 1rem !important;
            }
            .preview-modal h2 { font-size: 1.05rem !important; }
            .preview-modal p { font-size: 0.8rem !important; margin-bottom: 0.75rem !important; }
            .preview-modal a.preview-signup-btn { font-size: 14px !important; padding: 8px 24px !important; }
            .preview-modal p.preview-modal-login { font-size: 0.8rem !important; margin: 0.25rem 0 0 0 !important; }
          }
        `}</style>
        {isMobile ? (
          // Mobile: a full-length Trade Progress capture, scrolling naturally
          // like the Pedalboard/WantList previews. Replaces the fixed-height
          // portrait video whose clip-path/negative-margin sizing left a cream
          // gap on tall phones (first Android session's finding #2, resolved
          // per Jason's "make it an ordinary scrolling page" call).
          <img
            src="/images/trading-preview-mobile.png?v=5"
            alt="Trading on PedalxChange"
            // Blur on the element, not an overlay: an absolute overlay only
            // spans the fixed container's one-viewport box and scrolls away,
            // leaving everything below the first screenful sharp.
            style={{ width: '100%', display: 'block', filter: 'blur(3px)' }}
          />
        ) : (
          <video
            key="desktop"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            src="/images/trading-preview-desktop.mp4"
            style={{
              width: '100%',
              display: 'block',
              objectFit: 'cover',
              border: 'none',
              outline: 'none',
              clipPath: 'inset(3px 0 0 0)'
            }}
          />
        )}
        <div className="preview-modal" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 100,
          backgroundColor: 'rgba(255, 255, 255, 0.80)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: '16px',
          padding: '2.5rem 2rem',
          maxWidth: '420px',
          width: '90%',
          textAlign: 'center',
          fontFamily: 'Nunito, sans-serif',
          animation: 'modalPulse 3s ease-in-out infinite',
          border: '2px solid rgba(224, 224, 224, 0.7)'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#374151', marginBottom: '0.75rem' }}>
            See How Trading Works
          </h2>
          <p style={{ fontSize: '1rem', color: '#6B7280', marginBottom: '1.5rem', lineHeight: '1.5' }}>
            Sign up to list your pedals, make offers, and trade with other musicians.
          </p>
          <Link className="preview-signup-btn" to="/signup" style={{
            display: 'inline-block',
            backgroundColor: '#C9A227',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
            padding: '12px 32px',
            borderRadius: '9999px',
            textDecoration: 'none',
            fontFamily: 'Nunito, sans-serif',
            marginBottom: '1rem'
          }}>
            Sign Up Free
          </Link>
          <p className="preview-modal-login" style={{ fontSize: '0.9rem', color: '#6B7280' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#5A9ECB', fontWeight: 'bold', textDecoration: 'none' }}>Log In</Link>
          </p>
        </div>
      </div>
    );
  }

  const uniqueMatchCount = new Set(visibleMatchedPedals.map(m => m.matchedPedal?.id).filter(Boolean)).size;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
      {/* Background section */}
      <div style={{ backgroundColor: '#F7F3EB', flex: 1, overflowX: 'hidden' }}>
      <style>{`
        @media (min-width: 769px) {
          .trading-hub-container {
            max-width: 1100px !important;
          }
          .trading-hub-expanded-header {
            text-align: left !important;
          }
          .trading-hub-expanded-header h1 {
            text-align: left !important;
            margin: 0 0 0.5rem 0 !important;
          }
          .trading-hub-expanded-header p {
            text-align: left !important;
            margin: 0 !important;
          }
          .trading-hub-cards {
            width: 100%;
            min-width: 0;
          }
          .trading-hub-cards > a {
            display: block;
            width: 100%;
            min-width: 0;
          }
          .hub-card {
            max-width: 100% !important;
            width: 100% !important;
            box-sizing: border-box !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }
        @media (max-width: 768px) {
          .trading-hub-expanded-header {
            display: none !important;
          }
          .trading-hub-container {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
            padding-top: 1rem !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
          }
          .hub-card {
            width: calc(100vw - 1rem) !important;
            max-width: calc(100vw - 1rem) !important;
            box-sizing: border-box !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          .trading-hub-gap {
            height: 120px !important;
          }
        }
      `}</style>
      <div className="trading-hub-container" style={{ maxWidth: '900px', margin: '0 auto', paddingLeft: '2rem', paddingRight: '2rem', paddingTop: '2rem', paddingBottom: '2rem', width: '100%', boxSizing: 'border-box' }}>
        
        {/* Page Header — hidden on mobile; tab bar labels this section */}
        <div className="trading-hub-expanded-header" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 className="font-bold text-gray-900" style={{ fontSize: '2.625rem', fontWeight: '700', letterSpacing: '-0.025em', marginBottom: '0.5rem' }}>
            Trading
          </h1>
          <p className="text-gray-600" style={{ fontSize: '1.125rem' }}>
            Manage your trades, offers, and opportunities
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p className="text-gray-600">Loading...</p>
          </div>
        ) : (
          <>
            {currentUser && (
            <div className="trading-hub-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Matches Card */}
            <Link 
              to="/matches" 
              style={{ textDecoration: 'none' }}
            >
              <div 
                className="hub-card"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid #e5e7eb'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: uniqueMatchCount > 0 ? '1rem' : 0 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h2 className="font-semibold" style={{ fontSize: '1.5rem', color: '#111827', margin: 0 }}>
                        Matches
                      </h2>
                      {uniqueMatchCount > 0 && (
                        <span 
                          style={{
                            backgroundColor: '#3B82F6',
                            color: 'white',
                            borderRadius: '9999px',
                            padding: '0.25rem 0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          {uniqueMatchCount}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, color: '#5A9ECB' }}>
                      {uniqueMatchCount > 0 ? `View your ${uniqueMatchCount} match${uniqueMatchCount !== 1 ? 'es' : ''}` : 'See possible trades when a Wantlist overlaps your Pedalboard'}
                    </p>
                  </div>
                  <span style={{ fontSize: '1.25rem', color: '#9CA3AF' }}>→</span>
                </div>
                
                {/* Matched Pedals Preview */}
                {uniqueMatchCount > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.75rem', 
                    overflowX: 'auto',
                    paddingTop: '0.5rem',
                    paddingBottom: '0.25rem'
                  }}>
                    {visibleMatchedPedals
                      .filter((match, i, arr) =>
                        arr.findIndex(m => m.matchedPedal?.id === match.matchedPedal?.id) === i
                      )
                      .slice(0, 4)
                      .map((match, idx) => (
                      <div 
                        key={idx}
                        style={{
                          position: 'relative',
                          flex: '0 0 auto',
                          width: '120px'
                        }}
                      >
                        <img 
                          src={match.matchedPedal.images?.top || placeholderFor(match.matchedPedal).url} 
                          alt={`${match.matchedPedal.make} ${match.matchedPedal.model}`}
                          style={{ 
                            width: '120px',
                            height: '90px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '2px solid #C89F3C'
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          bottom: '4px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: '#C89F3C',
                          color: 'white',
                          fontSize: '0.65rem',
                          fontWeight: '600',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          whiteSpace: 'nowrap'
                        }}>
                          ✨ Match!
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Link>

            {/* Offers Card */}
            <Link 
              to="/trade-center" 
              style={{ textDecoration: 'none' }}
            >
              <div 
                className="hub-card"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid #e5e7eb'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* flex: 1 + minWidth: 0 + textAlign left: on phones the longer
                      "waiting for your response" line used to set this column's
                      width and the shorter blue line drifted off the left edge
                      (owner, 2026-11-02). */}
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h2 className="font-semibold" style={{ fontSize: '1.5rem', color: '#111827', margin: 0 }}>
                        Offers
                      </h2>
                      {offersAwaitingResponse > 0 && (
                        <span
                          style={{
                            backgroundColor: '#EF4444',
                            color: 'white',
                            borderRadius: '9999px',
                            padding: '0.25rem 0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          {offersAwaitingResponse}
                        </span>
                      )}
                    </div>
                    {offersAwaitingResponse > 0 && (
                      <p style={{ margin: '0 0 0.25rem', color: '#B91C1C', fontWeight: '600', textAlign: 'left' }}>
                        {offersAwaitingResponse === 1
                          ? '1 offer is waiting for your response'
                          : `${offersAwaitingResponse} offers are waiting for your response`}
                      </p>
                    )}
                    <p style={{ margin: 0, color: '#5A9ECB', textAlign: 'left' }}>
                      Received ({counts.receivedOffers}) • Sent ({counts.sentOffers})
                      {counts.openInterest > 0 && ` • Interested (${counts.openInterest})`}
                    </p>
                  </div>
                  <span style={{ fontSize: '1.25rem', color: '#9CA3AF' }}>→</span>
                </div>
              </div>
            </Link>

            {/* Trades Card */}
            <Link 
              to="/accepted-trades" 
              style={{ textDecoration: 'none' }}
            >
              <div 
                className="hub-card"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid #e5e7eb'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h2 className="font-semibold" style={{ fontSize: '1.5rem', color: '#111827', margin: 0 }}>
                        Trades
                      </h2>
                      {counts.acceptedTrades > 0 && (
                        <span 
                          style={{
                            backgroundColor: '#10B981',
                            color: 'white',
                            borderRadius: '9999px',
                            padding: '0.25rem 0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          {counts.acceptedTrades}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, color: '#5A9ECB' }}>
                      Accepted ({counts.acceptedTrades}) • Completed ({counts.completedTrades})
                    </p>
                  </div>
                  <span style={{ fontSize: '1.25rem', color: '#9CA3AF' }}>→</span>
                </div>
              </div>
            </Link>

            {/* Purchases Card */}
            <Link 
              to="/accepted-purchases" 
              style={{ textDecoration: 'none' }}
            >
              <div 
                className="hub-card"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid #e5e7eb'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h2 className="font-semibold" style={{ fontSize: '1.5rem', color: '#111827', margin: 0 }}>
                        Purchases
                      </h2>
                      {counts.acceptedPurchases > 0 && (
                        <span 
                          style={{
                            backgroundColor: '#8B5CF6',
                            color: 'white',
                            borderRadius: '9999px',
                            padding: '0.25rem 0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          {counts.acceptedPurchases}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, color: '#5A9ECB' }}>
                      Accepted ({counts.acceptedPurchases}) • Completed ({counts.completedPurchases})
                    </p>
                  </div>
                  <span style={{ fontSize: '1.25rem', color: '#9CA3AF' }}>→</span>
                </div>
              </div>
            </Link>

          </div>
            )}
          </>
        )}

      </div>

      {/* Fixed gap below content */}
      <div className="trading-hub-gap" style={{ height: '200px' }}></div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TradingHub;

